// The storefront's router: pjax-max 5 (vendored from ../pjax-max by
// scripts/vendor-pjax-max.mjs; its types are in vendor/pjax-max/). It swaps
// the [data-router-view] inside <main data-router-wrapper> in
// layout/theme.liquid, and keeps the rest of the document in step through
// pjax-head.js. Links route through one delegated listener, so links added
// at any time do, inside shadow roots too.
//
// Not in the theme editor: sections there are re-rendered by Shopify, and a
// router would fight it.
//
// Diagnostics: run `localStorage.setItem("pjax-max:debug", "1")` and reload
// to load pjax-max's (they say in the console why a navigation fell back to
// a page load, which transition ran, and the like); removeItem to stop.

import { Core, Transition } from "@theme/pjax-max";
import { ThemeEvents } from "@theme/events";
import { getLenis } from "@theme/utilities";
import { shouldExcludeFromPjax } from "@theme/pjax-routing";
import { adoptFirstViewStylesheets, adoptViewStylesheets, createShopifyHead, runViewScripts } from "@theme/pjax-head";
import { kickApps, watchApps } from "@theme/pjax-apps";

const DEBUG_KEY = "pjax-max:debug";

if (typeof window.Shopify === "undefined" || !window.Shopify.designMode) {
  initPjax().catch((error) => console.error("[pjax] failed to start", error));
}

async function initPjax() {
  const debug = wantsDebug() ? (await import("@theme/pjax-max-debug")).debug : undefined;
  const root = document.documentElement;
  const firstView = document.querySelector("[data-router-view]");

  // Routes that have to load in full (checkout, account, cart, app proxies:
  // see pjax-routing.js). pjax-max leaves a link alone when it or an ancestor
  // has data-router-disabled, which it reads as the click reaches its own
  // listener, on the document's bubble phase; this one is on the capture
  // phase, so it marks the link first. The theme's `on:click` handlers are
  // capture-phase too, and pjax-max skips a click they prevented.
  document.addEventListener(
    "click",
    (event) => {
      const link = event.composedPath().find((node) => node instanceof HTMLAnchorElement);
      if (!link?.hasAttribute("href")) return;
      if (shouldExcludeFromPjax(link.href)) {
        link.setAttribute("data-router-disabled", "excluded");
      } else if (link.getAttribute("data-router-disabled") === "excluded") {
        link.removeAttribute("data-router-disabled");
      }
    },
    true
  );

  // pjax-max swaps the view, never <body>, so the `{{ template.name }}` class
  // the layout puts on <body> (`body.index .slide__content` in theme.css)
  // would stay the first page's. The view's data-router-data carries the
  // template; track the class we own rather than diffing classList, since
  // other code adds and removes unrelated classes on <body>.
  let template = templateOf(firstView);
  const syncTemplateClass = ({ to }) => {
    const next = to.data.template;
    if (!next || next === template) return;
    if (template) document.body.classList.remove(template);
    document.body.classList.add(next);
    template = next;
  };

  const closeOverlays = () => {
    for (const dialog of document.querySelectorAll("dialog[open]")) {
      let host = dialog.parentElement;
      while (host && typeof host.closeDialog !== "function") host = host.parentElement;
      if (host) {
        try { host.closeDialog(); } catch {}
      } else {
        try { dialog.close(); } catch {}
      }
    }
  };

  // pjax-max restores scroll itself (manualScrollRestoration below): the top
  // or the #anchor for a link, where the page was left for back and forward.
  // Two things would undo its jumps. `html { scroll-behavior: smooth }` would
  // animate them, and the clamp as the old view leaves; it's set aside for
  // the length of a navigation. And Lenis, which smooth-scrolls the window
  // from its own rAF loop, would carry on an animation it had started; a stop
  // and start ends one (a Lenis the scroll lock holds stopped isn't
  // animating, and is left to the lock). Once the page is in, Lenis measures
  // it and takes up the position pjax-max left.
  const holdScroll = () => {
    root.style.scrollBehavior = "auto";
    const lenis = getLenis();
    if (lenis && !lenis.isStopped) {
      lenis.stop();
      lenis.start();
    }
  };
  const releaseScroll = () => {
    getLenis()?.resize();
    root.style.removeProperty("scroll-behavior");
  };

  // <page-transition> is the cover: it fades in on page:transition:start and
  // out on page:transition:end (page-transition.js). The out half is the base
  // one, which finishes at once; the in half removes the old view, then runs
  // the new one's scripts, which look things up in the document.
  class ThemeTransition extends Transition {
    async in({ from, to }) {
      from?.remove();
      runViewScripts(to);
    }
  }

  const dispatch = (type, detail = {}) =>
    document.dispatchEvent(new CustomEvent(type, { bubbles: true, detail: { ...detail, timestamp: Date.now() } }));

  adoptFirstViewStylesheets(firstView);
  watchApps();

  const pjax = new Core({
    transitions: { default: ThemeTransition },
    head: createShopifyHead(),
    manualScrollRestoration: true,
    // Every link that scrolls into view is prefetched (link-prefetch.js), so
    // the cache holds more pages than pjax-max's default of 10. Pages are
    // whole documents; unvisited prefetches still go stale after 5 minutes.
    cache: { size: 20 },
    onLeave: [
      () => {
        document.body.classList.add("page-loading");
        closeOverlays();
        holdScroll();
        dispatch(ThemeEvents.pageTransitionStart, { direction: "out" });
      },
    ],
    onEnter: [
      (detail) => {
        syncTemplateClass(detail);
        adoptViewStylesheets(detail.to.view);
        document.body.classList.remove("page-loading");
      },
    ],
    debug,
  });

  pjax.addEventListener("NAVIGATE_END", ({ detail }) => {
    releaseScroll();
    dispatch(ThemeEvents.pageTransitionEnd);
    dispatch(ThemeEvents.pageLoaded);
    kickApps(detail.to.view, detail.to.page);
  });

  window.__pjax = pjax;
  document.dispatchEvent(new CustomEvent("pjax:ready", { detail: { pjax } }));
}

function templateOf(view) {
  try {
    return JSON.parse(view?.dataset.routerData || "{}").template ?? null;
  } catch {
    return null;
  }
}

function wantsDebug() {
  try {
    return localStorage.getItem(DEBUG_KEY) === "1";
  } catch {
    return false;
  }
}
